import { defineCollection } from "@rebasepro/common";
/**
 * One inbox this CRM reads and sends from.
 *
 * As many as there are addresses worth keeping conversations for — the shared
 * `hello@`, a founder's own address, a sales alias — each read into the same
 * conversation list and each answered from itself. A conversation remembers the
 * mailbox it arrived in, so a reply goes out from the address the other side
 * wrote to, which is the only From they will recognise.
 *
 * ── The password is not in this table in any usable form ─────────────────────
 * `password_sealed` holds it encrypted under a key derived from a server secret
 * that never enters the database (see `backend/src/agent/inbox/credentials.ts`).
 * It is set through `POST /api/functions/inbox/mailboxes/:id/password` and
 * never read back by anything but the sync and the sender. The field is hidden
 * from every screen: an admin who opens the row sees whether a password is set,
 * not a string that looks like one.
 *
 * ── One login for both directions ────────────────────────────────────────────
 * `username` authenticates IMAP and SMTP alike, because every provider this was
 * built against (Google Workspace with an app password, Fastmail, iCloud) uses
 * the same credential for reading and sending. A server that needs two different
 * logins is rare enough to be a second mailbox row rather than four more fields
 * on every row.
 */
const mailboxesCollection = defineCollection({
    name: "Mailboxes",
    singularName: "Mailbox",
    slug: "mailboxes",
    table: "mailboxes",
    description: "The inboxes this CRM reads and sends from. Add one per address you keep conversations in.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        name: {
            name: "Name",
            type: "string",
            validation: { required: true },
            description: "What the inbox calls it — \"hello@\", \"Francesco\", \"Sales\""
        },
        address: {
            name: "Address",
            type: "string",
            validation: { required: true, unique: true },
            description: "The address mail is sent from, and the one people write to"
        },
        aliases: {
            name: "Also receives as",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Address" },
            description: "Other addresses that deliver into this same mailbox. Without them, mail to an alias lists the alias as somebody we are talking to."
        },
        from_name: {
            name: "Sender name",
            type: "string",
            description: "The name shown beside the address on mail sent from here"
        },
        color: {
            name: "Colour",
            type: "string",
            defaultValue: "blue",
            enum: [
                { id: "blue", label: "Blue", color: "blue" },
                { id: "green", label: "Green", color: "green" },
                { id: "purple", label: "Purple", color: "purple" },
                { id: "orange", label: "Orange", color: "orange" },
                { id: "teal", label: "Teal", color: "teal" },
                { id: "pink", label: "Pink", color: "pink" }
            ],
            description: "How its conversations are marked in the inbox when more than one mailbox is shown"
        },
        provider: {
            name: "Provider",
            type: "string",
            defaultValue: "gmail",
            enum: [
                { id: "gmail", label: "Google / Gmail", color: "red" },
                { id: "fastmail", label: "Fastmail", color: "blue" },
                { id: "icloud", label: "iCloud", color: "gray" },
                { id: "custom", label: "Other (IMAP/SMTP)", color: "gray" }
            ],
            description: "Fills in the servers. Custom leaves them to you."
        },
        username: {
            name: "Login",
            type: "string",
            validation: { required: true },
            description: "The account that signs in to read and send — for an alias, the account it belongs to"
        },
        imap_host: { name: "IMAP server", type: "string" },
        imap_port: { name: "IMAP port", type: "number", defaultValue: 993 },
        imap_secure: { name: "IMAP over TLS", type: "boolean", defaultValue: true },
        smtp_host: { name: "SMTP server", type: "string" },
        smtp_port: { name: "SMTP port", type: "number", defaultValue: 465 },
        smtp_secure: {
            name: "SMTP over TLS",
            type: "boolean",
            defaultValue: true,
            description: "On for port 465. Off for 587, which upgrades with STARTTLS."
        },
        password_sealed: {
            name: "Password (encrypted)",
            type: "string",
            admin: { readOnly: true, hideFromCollection: true },
            description: "Encrypted with a server key. Set it from the mailbox screen; it is never shown."
        },
        password_set_at: { name: "Password set", type: "date", admin: { readOnly: true } },
        inbox_folder: { name: "Inbox folder", type: "string", defaultValue: "INBOX" },
        sent_folder: {
            name: "Sent folder",
            type: "string",
            description: "Leave empty: it is looked up on the server. Name it only if the lookup gets it wrong."
        },
        backfill_days: {
            name: "Read back (days)",
            type: "number",
            defaultValue: 45,
            description: "How far back the FIRST read goes. Changing it later does nothing until the mailbox is reset."
        },
        sync_enabled: { name: "Read it", type: "boolean", defaultValue: true },
        send_enabled: { name: "Send from it", type: "boolean", defaultValue: true },
        outreach_default: {
            name: "Cold outreach goes from here",
            type: "boolean",
            defaultValue: false,
            description: "The mailbox a drafted outreach email is sent from unless you pick another. Keep cold mail off the address your customers depend on."
        },
        signature: {
            name: "Signature",
            type: "string",
            admin: { multiline: true },
            description: "Added under replies and new messages written from here. Not under outreach, which carries its own sign-off."
        },
        status: {
            name: "Status",
            type: "string",
            defaultValue: "new",
            enum: [
                { id: "new", label: "Not read yet", color: "gray" },
                { id: "ok", label: "Working", color: "green" },
                { id: "needs_password", label: "Needs a password", color: "orange" },
                { id: "error", label: "Failing", color: "red" },
                { id: "off", label: "Switched off", color: "gray" }
            ],
            admin: { readOnly: true },
            description: "Written by the sync after every read"
        },
        last_synced_at: { name: "Last read", type: "date", admin: { readOnly: true } },
        last_error: { name: "Last error", type: "string", admin: { readOnly: true, multiline: true } },
        needs_attention: {
            name: "Needs attention",
            type: "string",
            admin: { readOnly: true, multiline: true },
            description: "Something the sync works around but you should fix — an address this mailbox sends as that it has not been told about, most often"
        },
        created_at: { name: "Created", type: "date", autoValue: "on_create", admin: { readOnly: true } }
    },
    admin: {
        icon: "AtSign",
        group: "Inbox",
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        display: {
            title: "name",
            subtitle: "address",
            status: "status",
            date: "last_synced_at"
        },
        sort: ["created_at", "asc"],
        propertiesOrder: [
            "name", "address", "aliases", "from_name", "color",
            "provider", "username", "imap_host", "imap_port", "imap_secure",
            "smtp_host", "smtp_port", "smtp_secure", "password_set_at",
            "inbox_folder", "sent_folder", "backfill_days",
            "sync_enabled", "send_enabled", "outreach_default", "signature",
            "status", "last_synced_at", "last_error", "needs_attention", "created_at"
        ]
    }
});
export default mailboxesCollection;
